import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { Button } from "@/components/ui/button";
import { Sparkles, MessageCircle } from "lucide-react";

/**
 * HANDMADE SHOWCASE
 * - Ana sayfada hero'nun hemen altında, kategori stripinin üstünde
 * - Admin tarafından yönetilen "Günlük El Yapımı Ürünler" kartları
 * - Katalog ürünü değil → sepete eklenmez, WhatsApp'tan sipariş yönlendirir
 * - 0 kart varsa bölüm hiç render edilmez
 */
export default function HandmadeShowcase() {
  const { settings } = useApp();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/handmade");
        setItems(Array.isArray(r.data) ? r.data : []);
      } catch {
        setItems([]);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading || items.length === 0) return null;

  const waPhone = settings?.whatsapp_number || "905391095756";
  const waLink = (title) => `https://wa.me/${waPhone}?text=${encodeURIComponent(`Merhaba, "${title}" el yapımı ürününüzle ilgileniyorum.`)}`;

  return (
    <section
      data-testid="handmade-showcase"
      className="relative bg-gradient-to-b from-[#FAF7F2] to-white py-10 lg:py-14 border-b border-[#E8E2D5]"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-end justify-between gap-3 mb-5 flex-wrap">
          <div>
            <div className="inline-flex items-center gap-2 bg-[#D9653B]/10 text-[#B4522F] rounded-full px-3 py-1 mb-2 text-xs font-heading font-bold uppercase tracking-wider">
              <Sparkles className="w-3.5 h-3.5" />
              Taze · Günlük · El Yapımı
            </div>
            <h2 className="font-heading font-extrabold text-3xl sm:text-4xl lg:text-5xl text-[#1B2A22] leading-tight">
              Günlük El Yapımı Ürünler
            </h2>
            <p className="text-sm text-[#1B2A22]/70 mt-2 max-w-xl">
              Her gün taze, elimizde hazırladığımız özel lezzetler. Beğendiğin için WhatsApp'tan sipariş et.
            </p>
          </div>
        </div>

        <div
          data-testid="handmade-grid"
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5"
        >
          {items.map((it, i) => (
            <HandmadeCard key={it.id || i} item={it} waLink={waLink(it.title)} highlight={i === 0} />
          ))}
        </div>
      </div>
    </section>
  );
}

function HandmadeCard({ item, waLink, highlight }) {
  const priceStr = item.price_label
    ? item.price_label
    : (typeof item.price === "number" && item.price > 0)
      ? `₺${item.price.toFixed(2)}`
      : null;

  return (
    <article
      data-testid={`handmade-card-${item.id}`}
      className={`group relative bg-white border rounded-3xl overflow-hidden shadow-sm hover:shadow-xl transition ${highlight ? "lg:col-span-1 border-[#D9653B]/40" : "border-[#E8E2D5]"}`}
    >
      <div className="relative aspect-[4/3] overflow-hidden bg-[#1B2A22]/5">
        <img
          src={item.image_url}
          alt={item.title}
          loading="lazy"
          className="w-full h-full object-cover group-hover:scale-105 transition duration-500"
          onError={(e) => { e.currentTarget.style.visibility = "hidden"; }}
        />
        <div className="pointer-events-none absolute inset-x-0 bottom-0 h-20 bg-gradient-to-t from-black/50 to-transparent" />
        {priceStr && (
          <div className="absolute top-3 left-3 bg-white/95 backdrop-blur px-3 py-1 rounded-full text-xs font-heading font-extrabold text-[#D9653B] shadow">
            {priceStr}
          </div>
        )}
      </div>
      <div className="p-4 sm:p-5">
        <h3 className="font-heading font-extrabold text-lg sm:text-xl text-[#1B2A22] leading-tight">
          {item.title}
        </h3>
        {item.description && (
          <p className="text-sm text-[#1B2A22]/70 mt-1.5 leading-relaxed line-clamp-3">
            {item.description}
          </p>
        )}
        <a
          href={waLink}
          target="_blank"
          rel="noopener noreferrer"
          data-testid={`handmade-wa-${item.id}`}
          className="mt-4 inline-flex items-center justify-center gap-2 w-full h-11 bg-[#25D366] hover:bg-[#1EB757] text-white font-heading font-bold rounded-full transition"
        >
          <MessageCircle className="w-4 h-4" />
          WhatsApp'tan Sipariş Ver
        </a>
      </div>
    </article>
  );
}
